/**
 * Result screen after a successful generation — 2026-07-29 redesign.
 *
 * The reveal IS the product moment: after a 2–5 minute wait and a spent
 * credit, the poster unveils — blur-up + scale entrance over a soft amber
 * bloom that fades as the image sharpens (one orchestrated moment, ~900 ms,
 * disabled under prefers-reduced-motion). Everything after it stays quiet:
 * title, alignment chip, then actions in one clear hierarchy:
 *   • Print now          (primary — the poster's whole purpose is the wall)
 *   • Show in library    (secondary)
 *   • Generate similar / Start fresh (ghost row)
 * plus the one-tap rating bar (IIG loop's only visible ask).
 */

import { useTranslation } from 'react-i18next';
import AlignmentChip from '../../shared/AlignmentChip';
import PosterRatingBar from '../../common/PosterRatingBar';

export default function ResultScreen({
  poster,
  design,
  prompt,
  alignment,          // IIG Phase 4 — canonical alignment object (or null)
  viewerStateCode,    // IIG Phase 4 — teacher's USPS code, orients the chip copy
  onShowInLibrary,
  onPrintNow,
  onGenerateSimilar,
  onGenerateFresh,
  saving = false,     // Tier A5 (2026-09-08): backend done, library save still running — actions disabled
}) {
  const { t } = useTranslation('home');
  // CDN full-res first (Tier A5): it is available the moment the backend is
  // done and is sharper than the 300 px thumbnail or the client-side
  // d_thumbnail; the browser streams it while the save runs behind.
  const previewSrc = poster?.full_res_path || poster?.preview_path || design?.d_thumbnail || poster?.thumbnail_path;

  return (
    <div style={{
      fontFamily: 'inherit',
      maxWidth: 720, margin: '0 auto', padding: '24px 20px',
      color: 'rgba(27,37,80,0.855)',
    }}>
      {/* Headline — the moment, not a filing confirmation. */}
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '1.35rem', fontWeight: 800 }}>
          {t('result.readyTitle')}
        </h2>
        <div style={{ fontSize: '0.8rem', color: 'rgba(27,37,80,0.475)' }}>
          {saving ? t('result.savingSubtitle') : t('result.savedSubtitle')}
        </div>
      </div>

      {/* The reveal */}
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
        <div className="rsBloom" aria-hidden="true" />
        <div className="rsReveal" style={{
          borderRadius: 14, overflow: 'hidden',
          background: '#EEF0F6',
          boxShadow: '0 24px 70px rgba(27,37,80,0.17), 0 0 0 1px rgba(27,37,80,0.057)',
          maxWidth: '100%',
        }}>
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={poster?.title || t('result.posterAlt')}
              style={{ display: 'block', maxWidth: '100%', maxHeight: 560, objectFit: 'contain' }}
            />
          ) : (
            <div style={{ padding: 60, color: 'rgba(27,37,80,0.285)' }}>{t('result.noPreview')}</div>
          )}
        </div>
      </div>

      {/* Title + alignment */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        {poster?.title && (
          <div style={{ fontSize: '1.02rem', fontWeight: 700, marginBottom: 6 }}>{poster.title}</div>
        )}
        {alignment && (
          <AlignmentChip
            alignment={alignment}
            viewerStateCode={viewerStateCode}
            style={{ marginBottom: 6, display: 'inline-block' }}
          />
        )}
        {prompt && (
          <div style={{
            fontSize: '0.78rem', color: 'rgba(27,37,80,0.399)',
            fontStyle: 'italic', lineHeight: 1.4, maxWidth: 520, margin: '4px auto 0',
          }}>
            "{prompt.length > 140 ? prompt.slice(0, 137) + '…' : prompt}"
          </div>
        )}
      </div>

      {/* Primary actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={onPrintNow} disabled={saving} style={saving ? { ...primaryBtn, opacity: 0.55, cursor: 'wait' } : primaryBtn}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ marginRight: 7, verticalAlign: '-2px' }}>
            <path d="M6 9V3h12v6M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v7H6v-7z"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t('result.printNow')}
        </button>
        <button onClick={onShowInLibrary} disabled={saving} style={saving ? { ...secondaryBtn, opacity: 0.55, cursor: 'wait' } : secondaryBtn}>{t('result.showInLibrary')}</button>
      </div>

      {/* IIG Phase 5 — one-tap rating (spec §4.10; the loop's only visible ask). */}
      {poster?.id && <PosterRatingBar posterId={poster.id} source="result_screen" />}

      {/* Iterate actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={onGenerateSimilar} disabled={saving} style={saving ? { ...ghostBtn, opacity: 0.55, cursor: 'wait' } : ghostBtn}>{t('result.generateSimilar')}</button>
        <button onClick={onGenerateFresh} disabled={saving} style={saving ? { ...ghostBtn, opacity: 0.55, cursor: 'wait' } : ghostBtn}>{t('result.generateFresh')}</button>
      </div>

      <style>{`
        .rsBloom {
          position: absolute; inset: -60px; pointer-events: none;
          background: radial-gradient(ellipse at center, rgba(127,75,246,0.22), transparent 60%);
          opacity: 0;
        }
        @media (prefers-reduced-motion: no-preference) {
          .rsReveal { animation: rsIn 0.9s cubic-bezier(.2,.8,.2,1) both; }
          .rsBloom  { animation: rsBloomFade 1.6s ease-out both; }
        }
        @keyframes rsIn {
          from { opacity: 0; filter: blur(14px); transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; filter: blur(0);   transform: none; }
        }
        @keyframes rsBloomFade {
          0%   { opacity: 0; }
          25%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

const baseBtn = {
  padding: '13px 20px', borderRadius: 10, border: 'none',
  fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: 700,
  cursor: 'pointer', transition: 'filter 0.15s ease, background 0.15s ease',
  flex: '0 1 auto', minWidth: 170,
};

const primaryBtn = {
  ...baseBtn,
  background: 'linear-gradient(135deg, #7F4BF6, #9B6DFF)',
  color: '#FFFFFF',
  boxShadow: '0 6px 22px rgba(127,75,246,0.35)',
};

const secondaryBtn = {
  ...baseBtn,
  background: '#FFFFFF',
  color: 'rgba(27,37,80,0.855)',
  border: '1px solid rgba(27,37,80,0.114)',
};

const ghostBtn = {
  ...baseBtn,
  background: 'transparent',
  border: '1px solid rgba(27,37,80,0.114)',
  color: 'rgba(27,37,80,0.57)',
  fontWeight: 600,
  minWidth: 160,
};
